/**
 * AiWizardPanel — the during-call protocol/ICF Q&A wizard. The recruiter asks a
 * free-text question; the answer comes from the study's protocol + informed
 * consent only, with page citations. Ephemeral: the running history is held here
 * and sent back with each ask (no server-side conversation).
 *
 * Ported in spirit from the Site Intelligence chat. Citations render as chips;
 * clicking one opens the source document (the protocol/ICF) in a new tab.
 */
import { useEffect, useRef, useState } from 'react';
import { FileText, Loader2, Send, Sparkles } from 'lucide-react';
import api from '../../../services/api';

interface Source {
  page: number;
  quote: string;
  doc_type: string;
  document_id: string;
  document_title?: string;
}
interface Msg { role: 'user' | 'assistant'; content: string; sources?: Source[]; }

interface Props {
  studyId: string;
  /** Open a study document (protocol/ICF) — wired to the presigned-URL flow. */
  onOpenDoc?: (documentId: string) => void;
}

export default function AiWizardPanel({ studyId, onOpenDoc }: Props) {
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // New study → fresh conversation (citations + answers are study-scoped).
  useEffect(() => { setMessages([]); setError(null); }, [studyId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, loading]);

  const ask = async () => {
    const q = input.trim();
    if (!q || loading) return;
    setInput('');
    setError(null);
    const history = messages.map((m) => ({ role: m.role, content: m.content }));
    setMessages((m) => [...m, { role: 'user', content: q }]);
    setLoading(true);
    try {
      const res = await api.post(`/api/crm/studies/${studyId}/ai/ask`, { question: q, history });
      setMessages((m) => [...m, { role: 'assistant', content: res.data?.answer || '', sources: res.data?.sources || [] }]);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Could not get an answer — try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div ref={scrollRef} className="min-h-0 flex-1 space-y-3 overflow-y-auto pr-1">
        {messages.length === 0 && !loading && (
          <div className="flex flex-col items-center justify-center gap-2 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
            <Sparkles className="h-6 w-6 text-primary-500" />
            <p>Ask anything about this study's protocol or informed consent.</p>
            <p className="text-xs text-gray-400">Answers come from the documents only, with page citations.</p>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={m.role === 'user' ? 'flex justify-end' : 'flex justify-start'}>
            <div
              className={`max-w-[90%] whitespace-pre-wrap break-words rounded-2xl px-3 py-2 text-sm ${
                m.role === 'user'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-100'
              }`}
            >
              {m.content}
              {m.sources && m.sources.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {m.sources.map((s, j) => (
                    <button
                      key={j}
                      type="button"
                      onClick={() => onOpenDoc?.(s.document_id)}
                      title={s.quote}
                      className="inline-flex items-center gap-1 rounded-full bg-white/70 px-2 py-0.5 text-[11px] font-medium text-primary-700 transition-colors duration-150 hover:bg-white dark:bg-gray-800/70 dark:text-primary-300 dark:hover:bg-gray-800"
                    >
                      <FileText className="h-3 w-3" /> Page {s.page}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="inline-flex items-center gap-2 rounded-2xl bg-gray-100 px-3 py-2 text-sm text-gray-500 dark:bg-gray-700 dark:text-gray-300">
              <Loader2 className="h-4 w-4 animate-spin" /> Reading the protocol…
            </div>
          </div>
        )}
      </div>

      {error && <p className="mt-2 text-xs text-red-600 dark:text-red-400">{error}</p>}

      <div className="mt-2 flex items-end gap-2">
        <textarea
          rows={1}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); ask(); } }}
          placeholder="Ask about eligibility, visits, dosing, stipend…"
          className="min-h-[44px] flex-1 resize-none rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
        />
        <button
          type="button"
          onClick={ask}
          disabled={loading || !input.trim()}
          aria-label="Ask"
          className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg bg-primary-600 px-3 text-white transition-colors duration-150 hover:bg-primary-700 disabled:opacity-50"
        >
          <Send className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
